import React, { useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  parseAgentId,
  createVersionId,
  getNextVersionNumber,
  formatVersionForDisplay,
  createVersionedAgentData,
  getAgentVersions,
  createVersionName,
  getBaseAgentName,
} from '../utils/agentVersioning';
import { createLLMAgentAfterEdit } from '../dataaccess/createLLMAgentAfterEdit';
import { useErrorLogging } from '../hooks/useErrorLogging';
import { useUser } from '../contexts/useUser';

interface AgentVersionTabsProps {
  currentAgentId: string;
  allAgents: any[];
  currentAgent: any;
  isFormDirty?: boolean;
  getFormValues?: () => any;
  onVersionChange: (newAgentId: string) => void;
}

export const AgentVersionTabs: React.FC<AgentVersionTabsProps> = ({
  currentAgentId,
  allAgents,
  currentAgent,
  isFormDirty = false,
  getFormValues,
  onVersionChange,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logError } = useErrorLogging();
  const { project } = useUser();
  const [createVersionDialogOpen, setCreateVersionDialogOpen] = useState(false);
  const [confirmCreateVersionOpen, setConfirmCreateVersionOpen] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState<number | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  
  const { baseId, version: currentVersion } = parseAgentId(currentAgentId);
  const versions = getAgentVersions(allAgents, baseId);
  
  // Find the current tab index
  const currentTabIndex = versions.findIndex(agent => agent.id === currentAgentId);
  
  const createVersionMutation = useMutation({
    mutationFn: createLLMAgentAfterEdit,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      setCreateVersionDialogOpen(false);
      setConfirmCreateVersionOpen(false);
      
      // Navigate to the edit page for the new version
      if (result.data?.createLLMAgent?.id) {
        navigate(`/agents/edit/${result.data.createLLMAgent.id}`, { replace: true });
      }
    },
    onError: (error) => {
      logError('Error creating agent version', error as any);
    },
  });

  const createNewVersion = async () => {
    if (!currentAgent || !project) return;
    
    try {
      const nextVersion = getNextVersionNumber(
        versions.map(v => parseAgentId(v.id))
      );
      const newVersionId = createVersionId(baseId, nextVersion);
      
      // Clone from current form values if available (includes unsaved edits),
      // otherwise fall back to server state
      const sourceData = getFormValues
        ? { ...currentAgent, ...getFormValues() }
        : currentAgent;
      
      // Create the versioned agent data with proper naming
      const versionedAgentData = createVersionedAgentData(sourceData, newVersionId);
      // Override the name with proper version naming
      versionedAgentData.name = createVersionName(
        getBaseAgentName(sourceData.name || currentAgent.name),
        nextVersion
      );
      
      await createVersionMutation.mutateAsync({
        projectId: project.id,
        ...versionedAgentData,
      });
    } catch (error) {
      logError('Error creating new version', error as any);
    }
  };

  const handleCreateNewVersion = () => {
    if (!currentAgent || !project) return;

    if (isFormDirty) {
      setConfirmCreateVersionOpen(true);
      return;
    }

    createNewVersion();
  };

  const handleConfirmCreateVersion = () => {
    setConfirmCreateVersionOpen(false);
    createNewVersion();
  };

  const handleCancelCreateVersion = () => {
    setConfirmCreateVersionOpen(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue < versions.length) {
      if (isFormDirty) {
        setPendingTabIndex(newValue);
        setDiscardDialogOpen(true);
        return;
      }
      const selectedAgent = versions[newValue];
      onVersionChange(selectedAgent.id);
      navigate(`/agents/edit/${selectedAgent.id}`, { replace: true });
    }
  };

  const handleConfirmDiscard = () => {
    if (pendingTabIndex !== null && pendingTabIndex < versions.length) {
      const selectedAgent = versions[pendingTabIndex];
      onVersionChange(selectedAgent.id);
      navigate(`/agents/edit/${selectedAgent.id}`, { replace: true });
    }
    setDiscardDialogOpen(false);
    setPendingTabIndex(null);
  };

  const handleCancelDiscard = () => {
    setDiscardDialogOpen(false);
    setPendingTabIndex(null);
  };

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Agent Versions
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs
          value={currentTabIndex >= 0 ? currentTabIndex : false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {versions.length > 0 ? (
            versions.map((agent) => {
              const { version } = parseAgentId(agent.id);
              
              return (
                <Tab
                  key={agent.id}
                  label={formatVersionForDisplay(version)}
                />
              );
            })
          ) : (
            // Show current version tab even if it's the only one
            <Tab
              label={formatVersionForDisplay(parseAgentId(currentAgentId).version)}
            />
          )}
        </Tabs>
        
        <Tooltip title="Create New Version">
          <IconButton
            onClick={() => setCreateVersionDialogOpen(true)}
            color="primary"
            size="small"
            
          >            
            <AddIcon />
            &nbsp;New Version
          </IconButton>
        </Tooltip>
      </Box>

      {versions.length <= 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Create a new version to experiment with different settings while keeping this version unchanged
        </Typography>
      )}

      <Dialog
        open={createVersionDialogOpen}
        onClose={() => setCreateVersionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Agent Version</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            This will create a new version of "{currentAgent?.name}" with all current settings.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The new version will start with empty documents, conversations, and MCP servers.
            You can modify the new version independently of existing versions.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Next version:</strong> {formatVersionForDisplay(
              getNextVersionNumber(versions.map(v => parseAgentId(v.id)))
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateVersionDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewVersion}
            variant="contained"
            disabled={createVersionMutation.isPending}
          >
            {createVersionMutation.isPending ? 'Creating...' : 'Create Version'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmCreateVersionOpen}
        onClose={handleCancelCreateVersion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You have unsaved changes in this version.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The new version will be cloned from your current edits, but this version will still have unsaved changes until you save it separately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreateVersion} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmCreateVersion} variant="contained">
            Create Version From Current Edits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved changes confirmation dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={handleCancelDiscard}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You have unsaved changes to this version. Switching versions will discard them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDiscard} color="inherit">
            Stay on This Version
          </Button>
          <Button onClick={handleConfirmDiscard} variant="contained" color="warning">
            Discard and Switch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
